import { useMemo } from "react";
import { type QueryHookOptions } from "@apollo/client";
import { useGlobalAttributesQuery } from "#graphql/supergraphHooks";
import {
  type GlobalAttributesQuery,
  type GlobalAttributesQueryVariables,
} from "#graphql/supergraphTypes";
import { apiAttributeToGlobalAttribute } from "../mappers";
import type { GlobalAttribute } from "#common/types/vault";

export interface UseGlobalAttributesReturn {
  /** Presentational global-attribute catalog for `DocumentVault`'s `globalLib`. */
  globalLib: GlobalAttribute[];
  /**
   * Maps each vault global `key` -> backend `globalAttributeId`, so global
   * references can be persisted on save (pass to `useVaultSpaces`).
   */
  globalIdByKey: Record<string, string>;
  loading: boolean;
  error: ReturnType<typeof useGlobalAttributesQuery>["error"];
  refetch: ReturnType<typeof useGlobalAttributesQuery>["refetch"];
}

/**
 * Fetch the curated global-attribute catalog via the dedicated
 * `globalAttributes(includeSystemBound, includeStewardBound)` query (both
 * default true) and expose it in the two shapes the UI integration needs: the
 * presentational `globalLib`, and the `globalIdByKey` index that lets
 * `useVaultSpaces` persist global references.
 *
 * @example
 * const { globalLib, globalIdByKey } = useGlobalAttributes();
 * const { spaces, saveSpace } = useVaultSpaces({ globalLib, globalIdByKey });
 */
export const useGlobalAttributes = (
  variables?: GlobalAttributesQueryVariables,
  options?: Omit<
    QueryHookOptions<GlobalAttributesQuery, GlobalAttributesQueryVariables>,
    "variables"
  >,
): UseGlobalAttributesReturn => {
  const { data, loading, error, refetch } = useGlobalAttributesQuery({
    fetchPolicy: "no-cache",
    ...options,
    variables,
  });

  const attributes = useMemo(() => data?.globalAttributes ?? [], [data]);
  const globalLib = useMemo(() => attributes.map(apiAttributeToGlobalAttribute), [attributes]);
  const globalIdByKey = useMemo(
    () => Object.fromEntries(attributes.map((a) => [a.name, a.id])),
    [attributes],
  );

  return { globalLib, globalIdByKey, loading, error, refetch };
};
